import { useEffect, useState } from 'react'

import { identifyUser, initAnalytics } from '@onl-web/shared/analytics'
import { useBridge } from '@onl-web/shared/bridge'
import { useExitWebView } from '@onl-web/shared/hooks'

import { registerTokenFetcher, setToken } from '@/apis/tokenStore'
import { APP_REDIRECT_URL, IS_MOCK, MIXPANEL_DEBUG, MIXPANEL_TOKEN } from '@/constants'
import { setSentryUser } from '@/lib/sentry'

const APP_NAME = 'gacha'
const APP_VERSION = '0.0.0'

function bootstrapAnalytics(userId?: string) {
  initAnalytics({
    token: MIXPANEL_TOKEN,
    appName: APP_NAME,
    env: import.meta.env.MODE,
    appVersion: APP_VERSION,
    debug: MIXPANEL_DEBUG,
  })
  if (userId) {
    identifyUser(userId)
    setSentryUser(userId)
  }
}

type AuthStatus = 'loading' | 'ready'
const TOKEN_TIMEOUT_MS = 5000

const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> =>
  Promise.race([promise, new Promise<never>((_, reject) => setTimeout(() => reject(new Error('timeout')), ms))])

export function useInitAuth() {
  const { isReady, callHandler } = useBridge()
  const [authStatus, setAuthStatus] = useState<AuthStatus>('loading')
  const exitWebView = useExitWebView(APP_REDIRECT_URL)

  useEffect(() => {
    if (IS_MOCK) {
      setToken('mock-token')
      registerTokenFetcher(async () => 'mock-token')
      bootstrapAnalytics('mock-user')
      // mount 시 1회 mock 인증 초기화 — 외부 시스템(브릿지) 우회 경로라 useEffect 내 sync setState 정당
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAuthStatus('ready')
      return () => registerTokenFetcher(null)
    }

    // 비정상 접근 리다이렉트는 WebViewGuard가 담당
    if (!isReady) return

    let cancelled = false

    // 401 토큰 갱신 fetcher — Flutter 가 getAuthToken(v2)에서 만료 확인 후 재발급해 준다.
    // 초기 진입은 레거시 getToken(v1, safe-bottom 포함)을 유지하고 갱신만 v2 를 탄다.
    registerTokenFetcher(async () => {
      const { accessToken } = await withTimeout(callHandler('getAuthToken'), TOKEN_TIMEOUT_MS)
      return accessToken ?? null
    })

    withTimeout(callHandler('getToken'), TOKEN_TIMEOUT_MS)
      .then(async ({ token, bottom }) => {
        if (cancelled) return
        setToken(token)
        if (bottom !== undefined && bottom !== null) {
          document.documentElement.style.setProperty('--safe-bottom', `${bottom}px`)
        }

        const userInfo = await withTimeout(callHandler('getUserInfo'), TOKEN_TIMEOUT_MS).catch(() => undefined)
        if (cancelled) return
        bootstrapAnalytics(userInfo?.userId)
        setAuthStatus('ready')
      })
      .catch((error) => {
        if (cancelled) return
        if (import.meta.env.DEV) {
          console.error('[useInitAuth] getToken failed:', error)
        }
        exitWebView()
      })

    return () => {
      cancelled = true
      registerTokenFetcher(null)
    }
  }, [isReady, callHandler, exitWebView])

  return { authStatus }
}
